/**
 * The one place where the simulation's Z-up frame meets Three.js's Y-up frame.
 *
 * See SIM_UP_AXIS in constants.ts for why the simulation stays Z-up. Nothing outside this
 * module should ever swap an axis. If you find yourself writing `.y` where the C++ wrote
 * `coords[2]`, you are in the wrong frame — come back here instead.
 *
 * The mapping is a rotation, not a relabelling, so that handedness is preserved and
 * cross products keep their sign:
 *
 *     sim (x, y, z)  ->  render (x, z, -y)
 *     render (x, y, z) ->  sim  (x, -z, y)
 *
 * Both frames stay right-handed, so a rotation that is counter-clockwise in the simulation
 * is counter-clockwise on screen.
 */

import { Euler, Quaternion, Vector3 } from 'three';

/** Rotation taking the simulation frame to the render frame: -90 degrees about X. */
const SIM_TO_RENDER_ROTATION = new Quaternion().setFromEuler(new Euler(-Math.PI / 2, 0, 0));
const RENDER_TO_SIM_ROTATION = SIM_TO_RENDER_ROTATION.clone().invert();

/** Simulation position -> render position. Writes into `out` when given. */
export function simToRender(sim: Vector3, out = new Vector3()): Vector3 {
  return out.set(sim.x, sim.z, -sim.y);
}

/** Render position -> simulation position. Writes into `out` when given. */
export function renderToSim(render: Vector3, out = new Vector3()): Vector3 {
  return out.set(render.x, -render.z, render.y);
}

/** Simulation orientation -> render orientation. */
export function simToRenderQuat(sim: Quaternion, out = new Quaternion()): Quaternion {
  return out.copy(SIM_TO_RENDER_ROTATION).multiply(sim);
}

/** Render orientation -> simulation orientation. */
export function renderToSimQuat(render: Quaternion, out = new Quaternion()): Quaternion {
  return out.copy(RENDER_TO_SIM_ROTATION).multiply(render);
}
